import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../constants.js";
import {
  answersToMarkdown,
  debugMalformedChatUiAction,
  dispatchNarrativeFlowAction,
  replayBufferedCognitionSteps,
  type ChatAnswerWidget,
  type ChatUiAnswer,
  type ChatUiPlanProposal,
  type ChatUiPlanProposalStatus,
  type ChatUiQuestionnaire,
  type CognitionStep,
  type NarrativeFlowUiActionPayload,
  type UseChatUiActionsOptions,
  xPostToRail,
} from "./chatUiActionHelpers";
import { postUiActionAnswer } from "./chatUiActionPost";
import {
  clearPendingPlanProposal,
  clearPendingQuestionnaire,
  loadPendingPlanProposal,
  loadPendingQuestionnaire,
  savePendingPlanProposal,
  savePendingQuestionnaire,
} from "./chatUiActionPersistence";
import { UiActionAnswerError } from "./chatUiActionPost";
import {
  isNarrativeFlowChatUiAction,
  parseChatUiActionStep,
} from "../../../../backend-hono/src/types/chat-ui-actions";

const LOCAL_CHAT_UI_ACTION_BASE = "http://127.0.0.1:8080";

function getChatUiActionBaseUrl(): string {
  if (typeof window === "undefined") return API_BASE_URL;
  return window.location.protocol === "file:" || !!(window as any).electron
    ? LOCAL_CHAT_UI_ACTION_BASE
    : API_BASE_URL;
}

export type {
  ChatAnswerWidget,
  ChatUiAnswer,
  ChatUiPlanProposal,
  ChatUiPlanProposalStatus,
  ChatUiQuestionnaire,
  ChatUiRightRailPayload,
  ChatUiTodoItem,
  NarrativeFlowUiActionPayload,
} from "./chatUiActionHelpers";

export function useChatUiActions(
  requestId: string | null,
  options: UseChatUiActionsOptions,
) {
  const {
    onPlanDecisionContinuation,
    onNarrativeFlowAction,
    onQuestionnaireContinuation,
    onRightRail,
    surfaceId,
    workspaceId,
    onTodoDrawer,
  } = options;
  const [questionnaire, setQuestionnaire] =
    useState<ChatUiQuestionnaire | null>(() => loadPendingQuestionnaire());
  const [planProposal, setPlanProposal] = useState<ChatUiPlanProposal | null>(
    () => loadPendingPlanProposal(surfaceId, workspaceId),
  );
  const [answerWidgets, setAnswerWidgets] = useState<ChatAnswerWidget[]>([]);
  const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
  const [isSubmittingPlanDecision, setIsSubmittingPlanDecision] =
    useState(false);
  const seenStepKeysRef = useRef<Set<string>>(new Set());
  const approvedPlanRequestIdsRef = useRef<Set<string>>(new Set());

  const handleStep = useCallback(
    (step: CognitionStep) => {
      try {
        const parsed = parseChatUiActionStep(step);
        if (!parsed.ok) {
          if (parsed.shouldWarn)
            debugMalformedChatUiAction(parsed.reason, step);
          return;
        }
        const { action } = parsed;
        const detail = parsed.detail as any;

        const stableDetailId =
          detail.operationId ??
          detail.actionId ??
          (typeof step.detail === "string" ? step.detail : "");
        const stepKey = `${step.label ?? ""}:${action}:${String(stableDetailId)}`;
        if (seenStepKeysRef.current.has(stepKey)) return;
        seenStepKeysRef.current.add(stepKey);

        if (action === "open_todo_drawer") {
          onTodoDrawer?.({
            title: detail.title,
            items: Array.isArray(detail.items) ? detail.items : [],
          });
        }
        if (action === "open_right_rail") {
          onRightRail?.(detail);
        }
        if (action === "present_x_post" && detail.openRail !== false) {
          onRightRail?.(xPostToRail(detail));
        }
        if (isNarrativeFlowChatUiAction(action)) {
          const payload = {
            ...(detail as Record<string, unknown>),
            action,
          } as NarrativeFlowUiActionPayload;
          onNarrativeFlowAction?.(payload);
          dispatchNarrativeFlowAction(payload);
        }
        if (action === "approval_questions_resolved") {
          clearPendingQuestionnaire(String(detail.actionId));
          setQuestionnaire((current) =>
            current?.actionId === String(detail.actionId) ? null : current,
          );
          setIsSubmittingAnswers(false);
          return;
        }
        if (action === "plan_proposal_resolved") {
          if (detail.status === "approved" && requestId) {
            approvedPlanRequestIdsRef.current.add(requestId);
          }
          clearPendingPlanProposal(String(detail.actionId));
          setPlanProposal((current) =>
            current?.actionId === String(detail.actionId)
              ? {
                  ...current,
                  status: detail.status,
                  feedback:
                    typeof detail.feedback === "string"
                      ? detail.feedback
                      : current.feedback,
                }
              : current,
          );
          setIsSubmittingPlanDecision(false);
          return;
        }
        if (action === "plan_proposal") {
          if (requestId && approvedPlanRequestIdsRef.current.has(requestId)) {
            return;
          }
          const planWorkspaceId =
            typeof detail.workspaceId === "string"
              ? detail.workspaceId
              : (workspaceId ?? undefined);
          const next: ChatUiPlanProposal = {
            actionId: String(detail.actionId),
            title: String(detail.title ?? "Plan proposal"),
            summary:
              typeof detail.summary === "string" ? detail.summary : undefined,
            markdown: String(detail.markdown ?? ""),
            approvalLabel:
              typeof detail.approvalLabel === "string"
                ? detail.approvalLabel
                : undefined,
            denyLabel:
              typeof detail.denyLabel === "string"
                ? detail.denyLabel
                : undefined,
            surface:
              detail.surface === "narrativeflow" ? "narrativeflow" : "chat",
            workspaceId: planWorkspaceId,
            status: "pending",
          };
          setPlanProposal(next);
          savePendingPlanProposal(requestId, next);
        }
        if (action === "approval_questions") {
          const next = {
            actionId: String(detail.actionId),
            title: String(detail.title ?? "Harper needs approval"),
            summary:
              typeof detail.summary === "string" ? detail.summary : undefined,
            questions: Array.isArray(detail.questions) ? detail.questions : [],
          };
          setQuestionnaire(next);
          savePendingQuestionnaire(requestId, next);
        }
      } catch (error) {
        console.debug("[useChatUiActions] Failed to handle chat UI action", {
          error,
          step,
        });
      }
    },
    [onNarrativeFlowAction, onRightRail, onTodoDrawer, requestId, workspaceId],
  );

  useEffect(() => {
    if (!requestId) {
      const pending = loadPendingQuestionnaire();
      if (pending) setQuestionnaire(pending);
      setPlanProposal(loadPendingPlanProposal(surfaceId, workspaceId));
      setAnswerWidgets([]);
      seenStepKeysRef.current.clear();
      return;
    }

    seenStepKeysRef.current.clear();
    const pending = loadPendingQuestionnaire();
    if (pending) setQuestionnaire(pending);
    setPlanProposal(loadPendingPlanProposal(surfaceId, workspaceId));
    replayBufferedCognitionSteps(requestId, handleStep);
    const es = new EventSource(
      `${getChatUiActionBaseUrl()}/api/ai/cognition/stream?requestId=${encodeURIComponent(requestId)}`,
    );

    es.addEventListener("step", (event) => {
      try {
        handleStep(JSON.parse(event.data) as CognitionStep);
      } catch {
        /* ignore malformed stream payload */
      }
    });

    const onWindowStep = (event: Event) => {
      const detail = (
        event as CustomEvent<{ requestId?: string; step?: CognitionStep }>
      ).detail;
      if (detail?.requestId !== requestId || !detail.step) return;
      handleStep(detail.step);
    };
    window.addEventListener("fintheon:cognition-step", onWindowStep);

    es.addEventListener("done", () => es.close());
    es.onerror = () => es.close();
    return () => {
      es.close();
      window.removeEventListener("fintheon:cognition-step", onWindowStep);
    };
  }, [requestId, handleStep, surfaceId, workspaceId]);

  const resolveQuestionnaire = useCallback(
    async (status: "answered" | "cancelled", answers: ChatUiAnswer[]) => {
      if (!questionnaire) return;
      setIsSubmittingAnswers(true);
      try {
        try {
          await postUiActionAnswer(questionnaire.actionId, status, answers);
        } catch (error) {
          if (
            status === "answered" &&
            isStaleUiActionAnswer(error) &&
            onQuestionnaireContinuation
          ) {
            onQuestionnaireContinuation(
              buildQuestionnaireContinuationPrompt(questionnaire, answers),
            );
          } else {
            throw error;
          }
        }
        if (status === "answered") {
          setAnswerWidgets((current) => [
            ...current,
            {
              id: questionnaire.actionId,
              title: questionnaire.title,
              markdown: answersToMarkdown(questionnaire, answers),
              createdAt: Date.now(),
            },
          ]);
        }
        clearPendingQuestionnaire(questionnaire.actionId);
        setQuestionnaire(null);
      } finally {
        setIsSubmittingAnswers(false);
      }
    },
    [onQuestionnaireContinuation, questionnaire],
  );

  const submitPlanDecision = useCallback(
    async (
      status: Exclude<ChatUiPlanProposalStatus, "pending" | "expired">,
      feedback?: string,
    ) => {
      if (!planProposal || planProposal.status !== "pending") return;
      const trimmedFeedback = feedback?.trim();
      if (status === "approved" && requestId) {
        approvedPlanRequestIdsRef.current.add(requestId);
      }
      setIsSubmittingPlanDecision(true);
      setPlanProposal((current) =>
        current?.actionId === planProposal.actionId
          ? {
              ...current,
              status,
              feedback: trimmedFeedback || current.feedback,
            }
          : current,
      );
      try {
        try {
          await postUiActionAnswer(
            planProposal.actionId,
            status,
            [],
            trimmedFeedback || undefined,
          );
        } catch (error) {
          if (isStaleUiActionAnswer(error) && onPlanDecisionContinuation) {
            onPlanDecisionContinuation(
              buildPlanDecisionContinuationPrompt(
                planProposal,
                status,
                trimmedFeedback,
              ),
              status,
            );
          } else {
            throw error;
          }
        }
        clearPendingPlanProposal(planProposal.actionId);
      } catch (error) {
        if (status === "approved" && requestId) {
          approvedPlanRequestIdsRef.current.delete(requestId);
        }
        setPlanProposal((current) =>
          current?.actionId === planProposal.actionId
            ? { ...current, status: "pending" }
            : current,
        );
        throw error;
      } finally {
        setIsSubmittingPlanDecision(false);
      }
    },
    [onPlanDecisionContinuation, planProposal, requestId],
  );

  return {
    questionnaire,
    planProposal,
    answerWidgets,
    isSubmittingAnswers,
    isSubmittingPlanDecision,
    submitAnswers: (answers: ChatUiAnswer[]) =>
      resolveQuestionnaire("answered", answers),
    cancelQuestions: () => resolveQuestionnaire("cancelled", []),
    submitPlanDecision,
  };
}

function buildPlanDecisionContinuationPrompt(
  proposal: ChatUiPlanProposal,
  status: Exclude<ChatUiPlanProposalStatus, "pending" | "expired">,
  feedback?: string,
) {
  const decision =
    status === "approved"
      ? "The user approved the plan. Switch to Analyze mode and execute the approved NarrativeFlow run now."
      : "The user rejected the plan. Stay in Plan mode and revise it before executing.";
  return [
    "[Composer plan decision submitted]",
    "",
    "The original composer plan approval action was no longer pending in the backend, so treat this as the submitted plan decision payload.",
    decision,
    "Do not re-open the same Implement the Plan approval loop unless the revised plan materially changes or a new approval boundary is required.",
    "",
    `Plan: ${proposal.title}`,
    proposal.summary ? `Context: ${proposal.summary}` : "",
    feedback ? `User context: ${feedback}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

function isStaleUiActionAnswer(error: unknown) {
  if (error instanceof UiActionAnswerError) return error.status === 404;
  if (!(error instanceof Error)) return false;
  return /404|not found/i.test(error.message);
}

function buildQuestionnaireContinuationPrompt(
  questionnaire: ChatUiQuestionnaire,
  answers: ChatUiAnswer[],
) {
  const answerLines = answers.flatMap((answer) => [
    `## ${answer.label}`,
    answer.value.trim() || "_No answer provided._",
    "",
  ]);
  return [
    "[Composer clarification answers submitted]",
    "",
    "Continue the existing CAO/Harper request with these captured answers. The original composer UI action was no longer pending in the backend, so treat this as the submitted answer payload.",
    "Do not call ask_approval_questions or re-open this clarification drawer for these same questions. Acknowledge the captured answers, deliberate with the available NarrativeFlow and RiskFlow context, then draft Harper's own final plan or continue the approved run.",
    "",
    `Question set: ${questionnaire.title}`,
    questionnaire.summary ? `Context: ${questionnaire.summary}` : "",
    "",
    ...answerLines,
  ]
    .filter((line) => line !== "")
    .join("\n");
}
